import { useQuery } from '@tanstack/react-query';
import type { NewsEvent, NewsResponse } from '@contracts/news';
import { api } from '../lib/api';
import { humanize, relative } from '../lib/format';
import { useNow } from '../hooks/useNow';
import { Banner, Empty, Panel } from './Panel';

export function useNews(hoursBack = 12, hoursAhead = 72) {
  return useQuery({
    queryKey: ['news', hoursBack, hoursAhead],
    queryFn: () => api.get<NewsResponse>(`/api/v1/news/events?hours_back=${hoursBack}&hours_ahead=${hoursAhead}`),
    refetchInterval: 30_000,
  });
}

const IMPACT_LABEL: Record<NewsEvent['impact'], string> = { high: '▲▲ High', medium: '▲ Medium', low: '· Low' };

export function NewsPolicyBanner({ data }: { data: NewsResponse | undefined }) {
  if (!data) return null;
  const p = data.policy;
  if (p.state === 'clear') return null;
  return (
    <Banner
      tone={p.state === 'blackout' ? 'danger' : 'warn'}
      title={p.state === 'blackout' ? 'High-impact news window' : p.state === 'unavailable' ? 'News calendar unavailable' : 'Elevated news risk'}
    >
      {p.reason}. Expect fast moves and wider spreads.
    </Banner>
  );
}

export function NewsTimeline({ limit = 8, hoursAhead = 24 }: { limit?: number; hoursAhead?: number }) {
  const { data, isLoading, isError } = useNews(6, hoursAhead);
  const now = useNow(15_000);
  const events = (data?.events ?? []).filter((e) => e.impact !== 'low' || limit > 10).slice(0, limit);
  return (
    <Panel
      title="News impact timeline"
      meta={data?.isDemo ? <span className="badge badge--warn">DEMO DATA</span> : data?.provider}
    >
      {isLoading ? <p className="small muted">Loading calendar…</p> : null}
      {isError ? <p className="small muted">News calendar unavailable.</p> : null}
      {data && data.policy.state !== 'clear' ? (
        <p className="small" role="status" style={{ marginBottom: 8 }}>
          <span className={`badge ${data.policy.state === 'blackout' ? 'badge--danger' : 'badge--warn'}`}>
            {humanize(data.policy.state)}
          </span>{' '}
          {data.policy.reason}
        </p>
      ) : null}
      {data && !events.length ? <Empty title="No relevant events">Nothing scheduled in this window.</Empty> : null}
      {events.length ? (
        <ol className="timeline">
          {events.map((e) => (
            <li key={e.id} className={`is-${e.impact}`}>
              <div className="row small" style={{ justifyContent: 'space-between' }}>
                <strong>{e.title}</strong>
                <span className="muted num">{relative(e.eventTime, now)}</span>
              </div>
              <div className="small muted">
                {IMPACT_LABEL[e.impact]} · {e.currencies.join(', ')} · {humanize(e.phase)}
                {e.kind === 'unscheduled' ? ' · headline' : ''}
              </div>
            </li>
          ))}
        </ol>
      ) : null}
      <p className="disclaimer">News only reduces or blocks risk — the system never trades on a headline alone.</p>
    </Panel>
  );
}
